import { FlexContainer } from '@Components/CasTreeTransfer/Components';
import ConditionalTooltip from '@Components/Common/ConditionnalTooltip';
import TextEllipsis from '@Components/Common/Tooltip/TextEllipsis';
import { usePolicyContext, storeSelector } from '@Policy/store';
import { raiActionDataSelector } from '@Policy/store/filters/riskAI';
import {
  MergedRiskAiFilterRule,
  ExchangeOnlinePolicyItem,
  ActionEnum,
} from '@Policy/utils/types';
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  Text,
  Button,
  Flex,
  Icon,
  MenuItem,
  Scrollbar,
  SearchInput,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  TonicProps,
  useColorMode,
  useColorStyle,
  MenuItemProps,
  Divider,
  LinkButton,
} from '@tonic-one/react';
import {
  CollapseRightIcon,
  AngleRightIcon,
  AngleLeftIcon,
  CollapseLeftIcon,
} from '@tonic-one/react-icons';
import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EditableActionCell } from './EditableActionCell';
import { EditableActionModal } from './EditableActionModal';
import isEqual from 'lodash/isEqual';
import { handleRedirectClick } from '@Utils/common';

export const RuleSelection = (props: {
  allRules: MergedRiskAiFilterRule[];
  selectedRules: MergedRiskAiFilterRule[];
  description: string;
  leftContainerStyle?: TonicProps;
  rightContainerStyle?: TonicProps;
  editableActions?: boolean;
  onSelectionChange: (selectedRules: MergedRiskAiFilterRule[]) => void;
  rulesListLink?: string;
}) => {
  const {
    allRules,
    selectedRules,
    description,
    leftContainerStyle,
    rightContainerStyle,
    editableActions = true,
    onSelectionChange,
    rulesListLink,
  } = props;
  const [ROW_HEIGHT] = useState('48px');
  const [MAX_RULES_PER_POLICY] = useState(50);
  const contextStore = usePolicyContext();
  const contextSelector = storeSelector(contextStore);
  const { tmRaiFilterRules, setRiskAiState, setRiskAiRuleAction } =
    contextSelector((s) => {
      const template = s.template as ExchangeOnlinePolicyItem;
      return {
        tmRaiFilterRules: template.tmRiskAiFilters.tmRaiFilterRules,
        setRiskAiState: s.setRiskAiState,
        setRiskAiRuleAction: s.setRiskAiRuleAction,
      };
    });
  const { specifiedRulesActionOptions } = contextSelector(
    raiActionDataSelector,
    isEqual
  );

  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [ruleToView, setRuleToView] = useState<MergedRiskAiFilterRule | null>(
    null
  );
  const [leftSelectedKeys, setLeftSelectedKeys] = useState<string[]>([]);
  const [rightSelectedKeys, setRightSelectedKeys] = useState<string[]>([]);
  const [leftSearch, setLeftSearch] = useState('');

  const onClose = useCallback(() => {
    setRuleToView(null);
  }, []);

  const treeData = useMemo(
    () =>
      allRules
        .filter((rule) =>
          String(rule.tmRaiRule.raiRuleName)
            .toLowerCase()
            .includes(leftSearch.toLowerCase())
        )
        .map((rule) => ({
          key: rule.raiRuleId,
          title: rule.tmRaiRule.raiRuleName,
        })),
    [allRules, leftSearch]
  );

  const handleSelectLeftRules = useCallback(() => {
    const newKeys = leftSelectedKeys.filter(
      (key) => !selectedRules.some((rule) => rule.raiRuleId === key)
    );
    let newRules = [...selectedRules];
    newRules = newRules.concat(
      allRules.filter((rule) => newKeys.includes(rule.raiRuleId))
    );
    onSelectionChange(newRules);
    setLeftSelectedKeys([]); // clear selected keys
  }, [allRules, leftSelectedKeys, selectedRules, onSelectionChange]);

  const handleDeselectRightRule = useCallback(() => {
    const newRules = selectedRules.filter(
      (rule) => !rightSelectedKeys.includes(rule.raiRuleId)
    );
    onSelectionChange(newRules);
    setRightSelectedKeys([]); // clear selected keys
  }, [rightSelectedKeys, selectedRules, onSelectionChange]);

  const handleSelectAll = useCallback(() => {
    onSelectionChange(allRules);
    setLeftSelectedKeys([]); // clear selected keys
  }, [allRules, onSelectionChange]);

  const handleDeselectAll = useCallback(() => {
    onSelectionChange([]);
    setRightSelectedKeys([]); // clear selected keys
  }, [onSelectionChange]);

  const handleRuleActionLabelChange = useCallback(
    (rule: MergedRiskAiFilterRule, actionLabel: string) => {
      const newRule = { ...rule };
      if (newRule.raiRuleAction === ActionEnum.tagSubject) {
        newRule.raiRuleSubjectTag = actionLabel;
      } else if (newRule.raiRuleAction === ActionEnum.addDisclaimer) {
        newRule.raiRuleDisclaimer = actionLabel;
      }
      const newRules = [...tmRaiFilterRules];
      const index = newRules.findIndex(
        (r) => r.raiRuleId === newRule.raiRuleId
      );
      newRules[index] = newRule;
      setRiskAiState({ tmRaiFilterRules: newRules });
    },
    [tmRaiFilterRules, setRiskAiState]
  );

  const columns = useMemo(() => {
    if (!allRules) return [];
    const colHelper = createColumnHelper<MergedRiskAiFilterRule>();
    return [
      colHelper.accessor('tmRaiRule.raiRuleName', {
        header: () => <TextEllipsis>{t('policy.riskAi.rule')}</TextEllipsis>,
        cell: (cell) => <TextEllipsis>{cell.getValue()}</TextEllipsis>,
        minSize: 248,
      }),
      colHelper.display({
        id: 'action',
        header: () => <TextEllipsis>{t('common.label.action')}</TextEllipsis>,
        cell: (cell) => {
          const rule = cell.row.original;
          return (
            <EditableActionCell
              action={rule.raiRuleAction}
              options={specifiedRulesActionOptions}
              onActionChange={(action) =>
                setRiskAiRuleAction(rule.raiRuleId, action)
              }
              onModalOpen={() => {
                setRuleToView(rule);
              }}
            />
          );
        },
        minSize: 230,
        meta: {
          customColumnStyle: {
            width: '100%',
          },
        },
      }),
    ];
  }, [allRules, t, specifiedRulesActionOptions, setRiskAiRuleAction]);

  const table = useReactTable({
    data: selectedRules,
    columns,
    defaultColumn: {
      minSize: 48,
    },
    getCoreRowModel: getCoreRowModel(),
  });

  const toggleLeftItem = useCallback((raiRuleId: string) => {
    setLeftSelectedKeys((prev) =>
      prev.includes(raiRuleId)
        ? prev.filter((k) => k !== raiRuleId)
        : [...prev, raiRuleId]
    );
  }, []);

  const toggleRightItem = useCallback((raiRuleId: string) => {
    setRightSelectedKeys((prev) =>
      prev.includes(raiRuleId)
        ? prev.filter((k) => k !== raiRuleId)
        : [...prev, raiRuleId]
    );
  }, []);

  return (
    <>
      <Flex
        display="flex"
        alignItems="stretch"
        columnGap="3x"
        width="max-content"
        height="415px"
      >
        <FlexContainer direction="column" width={560} {...leftContainerStyle}>
          <Stack gap="2x" height="100%">
            <Stack flexDirection="row" gap="2x">
              <Text>{t('policy.riskAi.anomalies.availableRules')}</Text>
              {!!rulesListLink && (
                <LinkButton
                  onClick={() => {
                    handleRedirectClick(rulesListLink, '_blank');
                  }}
                >
                  ({allRules.length})
                </LinkButton>
              )}
            </Stack>
            <Text color={colorStyle.color.secondary}>
              {description}
              {/* {t('policy.riskAi.anomalies.availableRules.desc')} */}
            </Text>
            <SearchInput
              value={leftSearch}
              onChange={(e) => setLeftSearch(e.target.value)}
              onClearInput={() => setLeftSearch('')}
              placeholder={t('policy.riskAi.anomalies.search')}
              flexShrink={0}
            />
            <Scrollbar
              display="flex"
              flexDirection="column"
              overflowY="auto"
              minThumbHeight={150}
              height="100%"
            >
              {treeData.map(({ key, title }) => {
                return (
                  <ListItem
                    key={key}
                    title={title}
                    height="36px"
                    onClick={() => toggleLeftItem(key)}
                    disabled={selectedRules.some(
                      (rule) => rule.raiRuleId === key
                    )}
                    isSelected={leftSelectedKeys.includes(key)}
                  >
                    {title}
                  </ListItem>
                );
              })}
            </Scrollbar>
          </Stack>
        </FlexContainer>
        <Stack my="auto" alignItems="center" gap="2x">
          <Button
            variant="secondary"
            width="24px"
            height="max-content"
            size="sm"
            onClick={handleSelectAll}
          >
            <Icon as={CollapseRightIcon} />
          </Button>
          <ConditionalTooltip
            condition={leftSelectedKeys.length > MAX_RULES_PER_POLICY}
            label={t('policy.riskAi.anomalies.maxRulesExceeded', {
              max: MAX_RULES_PER_POLICY,
            })}
            placement="top"
          >
            <Button
              variant="secondary"
              onClick={handleSelectLeftRules}
              width="24px"
              height="max-content"
              size="sm"
              disabled={leftSelectedKeys.length > MAX_RULES_PER_POLICY}
            >
              <Icon
                as={AngleRightIcon}
                color={
                  leftSelectedKeys.length > 0
                    ? colorStyle.color.info
                    : colorStyle.color.primary
                }
              />
            </Button>
          </ConditionalTooltip>
          <Button
            variant="secondary"
            onClick={handleDeselectRightRule}
            width="24px"
            height="max-content"
            size="sm"
          >
            <Icon
              as={AngleLeftIcon}
              color={
                rightSelectedKeys.length > 0
                  ? colorStyle.color.info
                  : colorStyle.color.primary
              }
            />
          </Button>
          <Button
            variant="secondary"
            width="24px"
            height="max-content"
            size="sm"
            onClick={handleDeselectAll}
          >
            <Icon as={CollapseLeftIcon} />
          </Button>
        </Stack>
        <FlexContainer direction="column" width={560} {...rightContainerStyle}>
          <Text>{t('policy.riskAi.anomalies.selectedRules')}</Text>
          {selectedRules.length > 0 && editableActions && (
            <Table layout="flexbox" minWidth="100%" height="100%">
              <TableHeader position="sticky" top={0} zIndex={1}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    width="100%"
                    alignItems="end"
                    key={`header-group-${headerGroup.id}`}
                  >
                    {headerGroup.headers.map((header) => (
                      <TableCell
                        key={`header-${header.id}`}
                        width={header.getSize()}
                        minWidth={header.column.columnDef.minSize}
                        {...header.column.columnDef.meta?.customColumnStyle}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <Scrollbar
                display="flex"
                flexDirection="column"
                flex="1 1 auto"
                overflowY="auto"
                minThumbHeight={150}
                height="100%"
              >
                <TableBody height="100%">
                  {table.getRowModel().rows.map((row) => (
                    <ListItem
                      px="0px"
                      py="0px"
                      isSelected={rightSelectedKeys.includes(
                        row.original.raiRuleId
                      )}
                      onClick={() => toggleRightItem(row.original.raiRuleId)}
                    >
                      <TableRow
                        width="100%"
                        height={ROW_HEIGHT}
                        key={`row-${row.id}`}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={`cell-${cell.id}`}
                            width={cell.column.getSize()}
                            minWidth={cell.column.columnDef.minSize}
                            display="flex"
                            alignItems="center"
                            {...cell.column.columnDef.meta?.customColumnStyle}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    </ListItem>
                  ))}
                </TableBody>
              </Scrollbar>
            </Table>
          )}
          {selectedRules.length > 0 && !editableActions && (
            <Stack height="100%">
              <Divider my="2x" />
              <Scrollbar
                display="flex"
                flexDirection="column"
                overflowY="auto"
                minThumbHeight={150}
                height="100%"
              >
                {selectedRules.map(({ raiRuleId, tmRaiRule }) => {
                  return (
                    <ListItem
                      key={raiRuleId}
                      title={tmRaiRule.raiRuleName}
                      height="36px"
                      onClick={() => toggleRightItem(raiRuleId)}
                      isSelected={rightSelectedKeys.includes(raiRuleId)}
                    >
                      {tmRaiRule.raiRuleName}
                    </ListItem>
                  );
                })}
              </Scrollbar>
            </Stack>
          )}
          {selectedRules.length === 0 && (
            <Flex flex="auto" justifyContent="center" alignItems="center">
              <Text color={colorStyle.color.secondary}>
                {t('common.noData')}
              </Text>
            </Flex>
          )}
        </FlexContainer>
      </Flex>
      {ruleToView && (
        <EditableActionModal
          action={ruleToView.raiRuleAction}
          subjectTag={ruleToView.raiRuleSubjectTag}
          disclaimer={ruleToView.raiRuleDisclaimer}
          name={ruleToView.tmRaiRule.raiRuleName}
          isOpen={!!ruleToView}
          onClose={onClose}
          onConfirm={(newLabel) => {
            handleRuleActionLabelChange(ruleToView, newLabel);
            onClose();
          }}
        />
      )}
    </>
  );
};

const ListItem = (props: { isSelected: boolean } & MenuItemProps) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  return (
    <MenuItem
      backgroundColor={
        props.isSelected ? colorStyle.background.tertiary : 'transparent'
      }
      cursor="pointer"
      _focusActive={{
        backgroundColor: colorStyle.background.fourth,
      }}
      onClick={props.onClick}
      {...props}
    >
      {props.children}
    </MenuItem>
  );
};
